<script lang="ts" setup>
import type { BrokerUpdatesOrderDto, TravelOrderWithTruck, TripOverview, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { API } from "@/api/api";
import type { TripUpdate } from "@/api/trips/dto/update";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import type { CheckCallDTO, CheckCallWithUpdate } from "@/api/trips/dto/check-call";
import CreateBrokerUpdateForm from "@/views/trips/components/forms/CreateBrokerUpdateForm.vue";
import ViewUpdateDialog from "@/views/trips/components/dialogs/ViewUpdateDialog.vue";
import TravelOrder from "@/views/trips/view/components/TravelOrder.vue";
import BrokerUpdatesTable, { type CheckCallTruckInfo } from "@/views/trips/view/components/BrokerUpdatesTable.vue";
import { useSnackbarStore } from "@/store/snackbar.store";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import { useTripStore } from "@/store/trip.store";
import { usePipStore } from "@/store/pip.store";
import { RouteStatus } from "@/constants/trip";
import dayjs from "dayjs";
import type { BrokerUpdateFormProps } from "@/types/props";

const api = new API();

const location = useRoute();
const tripStore = useTripStore();
const pipStore = usePipStore();
const { showSnackbarError, showSnackbarSuccess } = useSnackbarStore();

const props = defineProps<{
  orders: BrokerUpdatesOrderDto[];
  routes: TripRouteDto[];
}>();

const emits = defineEmits<{
  (e: "update"): void;
}>();

const checkCalls = ref<CheckCallWithUpdate[]>([]);
const selectedTravelOrder = ref<TravelOrderWithTruck | null>(null);
const selectedCheckCall = ref<CheckCallDTO | null>(null);
const updateInfo = ref<TripUpdate | null>(null);
const showViewUpdateDialog = ref<boolean>(false);
const showIntervalDialog = ref<boolean>(false);

const isAllMode = computed(() => !location.params.orderId);

const displayedTravelOrders = computed<TravelOrderWithTruck[]>(() => {
  if (location.params.orderId) {
    const order = props.orders.find(({ id }) => id === location.params.orderId);
    return order?.travelOrders ?? [];
  }

  return [];
});

const allCheckCalls = computed<CheckCallWithUpdate[]>(() => {
  return [...checkCalls.value].reverse();
});

function resolveTravelOrderForCheckCall(checkCall: CheckCallWithUpdate): TravelOrderWithTruck | undefined {
  const allTravelOrders = props.orders.flatMap((order) => order.travelOrders);

  if (checkCall.travelOrderIds?.length) {
    const matched = allTravelOrders.find((to) => to.id && checkCall.travelOrderIds!.includes(to.id));

    if (matched) {
      return matched;
    }
  }

  if (checkCall.routeId) {
    return allTravelOrders.find((to) => to.routeId === checkCall.routeId);
  }

  return undefined;
}

function isCheckCallCreateDisabled(checkCall: CheckCallWithUpdate): boolean {
  if (checkCall.deletedBy) {
    return true;
  }

  const route = props.routes.find((r) => r.id === checkCall.routeId);

  return route?.status === RouteStatus.planned;
}

function resolveTruckInfo(checkCall: CheckCallWithUpdate): CheckCallTruckInfo | undefined {
  const travelOrder = resolveTravelOrderForCheckCall(checkCall);

  if (!travelOrder?.id) {
    return undefined;
  }

  const order = props.orders.find((o) => o.travelOrders.some((to) => to.id === travelOrder.id));

  if (!order) {
    return undefined;
  }

  return {
    truckId: travelOrder.truck.id,
    truckNumber: travelOrder.truck.number,
    travelOrderId: travelOrder.id,
    orderNumber: order.number,
  };
}

const tripId = computed(() => {
  return location.params.id.toString();
});

function openCreateUpdate(travelOrder: TravelOrderWithTruck, checkCall: CheckCallDTO) {
  selectedTravelOrder.value = travelOrder;
  selectedCheckCall.value = checkCall;

  const brokerUpdateProps: BrokerUpdateFormProps = {
    checkCallId: checkCall.id,
    checkCallType: checkCall.type,
    routeId: travelOrder.routeId!,
    travelOrderId: travelOrder.id!,
    orders: props.orders,
  };

  pipStore.open({
    component: CreateBrokerUpdateForm,
    props: brokerUpdateProps,
    on: {
      success: onUpdateCreation,
      close: () => pipStore.close(),
    },
  });
}

function openIntervalDialog(travelOrder: TravelOrderWithTruck) {
  showIntervalDialog.value = true;
  selectedTravelOrder.value = travelOrder;
}

async function loadCheckCalls(): Promise<void> {
  try {
    const response = await api.trips.getCheckCalls(tripId.value);

    if (response.success) {
      checkCalls.value = response.data?.checkCalls || [];
    }
  } catch (error) {
    throw new Error("Could not load check calls");
  }
}

async function loadUpdates(): Promise<void> {
  try {
    const response = await api.trips.getUpdates(tripId.value);
    const updates = response.data?.updates || [];

    checkCalls.value = checkCalls.value.map((checkCall) => {
      const update = getUpdateForCheckCall(updates, checkCall);

      return {
        ...checkCall,
        update: update,
      };
    });
  } catch (error) {
    throw new Error("Could not load broker updates");
  }
}

function getUpdateForCheckCall(updates: TripUpdate[], checkCall: CheckCallWithUpdate): TripUpdate | undefined {
  const typesWithoutCheckingTravelOrderId = ["check-call", "dispatch-confirmed"];

  if (typesWithoutCheckingTravelOrderId.includes(checkCall.type)) {
    return updates.find((update) => update.checkCallId === checkCall.id);
  } else {
    return updates.find(
      (update) => update.checkCallId === checkCall.id && checkCall.travelOrderIds?.includes(update.travelOrderId),
    );
  }
}

async function viewUpdate(item: CheckCallWithUpdate) {
  const updateId = item.update?.id;
  const routeId = item.routeId;
  selectedCheckCall.value = item;

  if (!updateId || !routeId) {
    return;
  }

  try {
    const response = await api.events.loadBrokerUpdate(routeId, updateId);
    if (response?.data && response.success) {
      updateInfo.value = response.data;
      showViewUpdateDialog.value = true;
    }
  } catch (error) {
    showSnackbarError("Could not load update");
  }
}

async function updateInterval(interval: number | null): Promise<void> {
  if (!selectedTravelOrder.value || !selectedTravelOrder.value.id) {
    return;
  }

  try {
    const response = await api.trips.updateTravelOrderInterval(
      Number(tripId.value),
      selectedTravelOrder.value.id,
      interval,
    );

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
      emits("update");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

function getRouteByTravelOrderId(id: number | undefined) {
  return props.routes.find((route) => route.travelOrders.some((to) => to.id === id));
}

async function init() {
  await loadCheckCalls();
  await loadUpdates();
}

async function onUpdateCreation(newUpdate: TripUpdate, travelOrderId: number) {
  console.log("on update creation");
  checkCalls.value = checkCalls.value.map((checkCall) => {
    if (checkCall.id === newUpdate.checkCallId) {
      return {
        ...checkCall,
        update: newUpdate,
      };
    }

    return checkCall;
  });

  if (!tripStore.tripOverview) {
    return;
  }

  const updatedTrip: TripOverview = {
    ...tripStore.tripOverview,
    routes: tripStore.tripOverview.routes.map((route) => {
      return {
        ...route,
        travelOrders: route.travelOrders.map((to) => {
          if (to.id === travelOrderId) {
            return {
              ...to,
              lastUpdateTime: dayjs().toString(),
            };
          }

          return to;
        }),
      };
    }),
  };

  tripStore.updateTripOverview(updatedTrip);
}

onMounted(async () => {
  await init();
});
</script>

<template>
  <broker-updates-table
    v-if="isAllMode"
    :check-calls="allCheckCalls"
    :is-create-disabled="isCheckCallCreateDisabled"
    :resolve-travel-order="resolveTravelOrderForCheckCall"
    :resolve-truck-info="resolveTruckInfo"
    class="mt-11"
    @view-update="viewUpdate"
    @open-create-update="openCreateUpdate"
  />

  <template v-else>
    <div v-for="travelOrder of displayedTravelOrders" :key="travelOrder.id" class="mt-11">
      <travel-order
        :check-calls="checkCalls"
        :route="getRouteByTravelOrderId(travelOrder.id)"
        :travel-order="travelOrder"
        @update-monitor="emits('update')"
        @open-create-update="openCreateUpdate"
        @view-update="viewUpdate"
        @open-interval-dialog="openIntervalDialog"
      />
    </div>
  </template>

  <view-update-dialog
    v-if="updateInfo && selectedCheckCall"
    v-model="showViewUpdateDialog"
    :check-call-type="selectedCheckCall?.type"
    :update="updateInfo"
  />

  <trip-interval-dialog
    v-if="selectedTravelOrder"
    v-model="showIntervalDialog"
    :interval="selectedTravelOrder.updateInterval"
    :truck-id="selectedTravelOrder.truck.id"
    :truck-number="selectedTravelOrder.truck.number"
    title="Broker update settings"
    @update-interval="updateInterval"
  />
</template>

<style lang="scss" scoped></style>
